import React, { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { OrgProvider } from './contexts/OrgContext';
import { api } from './lib/api';
import Layout from './components/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Download from './pages/Download';
import Dashboard from './pages/Dashboard';
import FamilyHome from './pages/FamilyHome';
import Patients from './pages/Patients';
import PatientDetail from './pages/PatientDetail';
import Rooms from './pages/Rooms';
import Buildings from './pages/Buildings';
import RoomDetail from './pages/RoomDetail';
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Events from './pages/Events';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import Team from './pages/Team';
import Reports from './pages/Reports';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import AdminUsers from './pages/admin/AdminUsers';
import AdminPatients from './pages/admin/AdminPatients';
import AdminRooms from './pages/admin/AdminRooms';
import AdminBuildings from './pages/admin/AdminBuildings';
import AdminDevices from './pages/admin/AdminDevices';
import AdminDeviceTypes from './pages/admin/AdminDeviceTypes';
import AdminAlerts from './pages/admin/AdminAlerts';
import AdminQA from './pages/admin/AdminQA';
import AdminGateways from './pages/admin/AdminGateways';
import GatewayDetail from './pages/GatewayDetail';

function ProtectedRoute({ children }) {
  const { user, checked } = useAuth();
  if (!checked) {
    return <div className="min-h-screen grid place-items-center text-ink-muted">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, checked } = useAuth();
  if (!checked) {
    return <div className="min-h-screen grid place-items-center text-ink-muted">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  return user?.role === 'family' ? <Navigate to="/" replace /> : children;
}

// Family users with a single linked patient go straight to that page.
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === 'family') return <FamilyHome />;
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/download" element={<Download />} />
      <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route path="/patients" element={<ProtectedRoute><Patients /></ProtectedRoute>} />
      <Route path="/patients/:id" element={<ProtectedRoute><PatientDetail /></ProtectedRoute>} />
      {/* Legacy /residents URLs keep working */}
      <Route path="/residents" element={<Navigate to="/patients" replace />} />
      <Route path="/residents/:id" element={<RedirectPatient />} />
      <Route path="/rooms" element={<ProtectedRoute><StaffOnly><Rooms /></StaffOnly></ProtectedRoute>} />
      <Route path="/buildings" element={<ProtectedRoute><StaffOnly><Buildings /></StaffOnly></ProtectedRoute>} />
      <Route path="/rooms/:id" element={<ProtectedRoute><StaffOnly><RoomDetail /></StaffOnly></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><Devices /></ProtectedRoute>} />
      <Route path="/devices/:id" element={<ProtectedRoute><DeviceDetail /></ProtectedRoute>} />
      <Route path="/gateways/:id" element={<ProtectedRoute><GatewayDetail /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Administration — admin role required, gated server-side as well. */}
      <Route path="/admin" element={<Navigate to="/admin/organizations" replace />} />
      <Route path="/admin/organizations" element={<AdminRoute><AdminOrganizations /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/patients" element={<AdminRoute><AdminPatients /></AdminRoute>} />
      <Route path="/admin/rooms" element={<AdminRoute><AdminRooms /></AdminRoute>} />
      <Route path="/admin/buildings" element={<AdminRoute><AdminBuildings /></AdminRoute>} />
      <Route path="/admin/devices" element={<AdminRoute><AdminDevices /></AdminRoute>} />
      <Route path="/admin/device-types" element={<AdminRoute><AdminDeviceTypes /></AdminRoute>} />
      <Route path="/admin/alerts" element={<AdminRoute><AdminAlerts /></AdminRoute>} />
      <Route path="/admin/gateways" element={<AdminRoute><AdminGateways /></AdminRoute>} />
      <Route path="/admin/qa" element={<AdminRoute><AdminQA /></AdminRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RedirectPatient() {
  // Preserve deep-links from old /residents/:id URLs.
  const id = window.location.pathname.split('/').filter(Boolean).pop();
  return <Navigate to={`/patients/${id}`} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <OrgProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          {/* Global Toaster — every page can call `toast()` and have it rendered
              without mounting its own Toaster. Individual page Toasters still
              work but are now redundant. */}
          <Toaster position="top-right" richColors />
        </OrgProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
